import type { SubscriptionStatus } from '@viome/viome-base/src/usecase/SMP/SMP.model';
import { getFormulaImageUrl } from '@/utils/nutrition/getFormulaImageUrl';

// ============================================
// Subscription Status Helpers
// ============================================

export const SUBSCRIPTION_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  SKIPPED: 'skipped',
  CANCELLED: 'cancelled',
  INACTIVE: 'inactive',
} as const;

export const isSubscriptionActive = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.ACTIVE;

export const isSubscriptionPaused = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.PAUSED;

export const isSubscriptionSkipped = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.SKIPPED;

/** API returns 'inactive' for cancelled subscriptions (legacy: SUBSCRIPTION_CANCELLED = 'inactive') */
export const isSubscriptionCancelled = (status: SubscriptionStatus): boolean =>
  status === SUBSCRIPTION_STATUS.CANCELLED || status === SUBSCRIPTION_STATUS.INACTIVE;

/** Returns true if subscription is in an active-like state (active, paused, or skipped) */
export const isSubscriptionActiveState = (status: SubscriptionStatus): boolean =>
  isSubscriptionActive(status) || isSubscriptionPaused(status) || isSubscriptionSkipped(status);

// ============================================
// Status Badge Styles
// ============================================

export const getStatusBadgeClassName = (status: SubscriptionStatus): string => {
  switch (status) {
    case SUBSCRIPTION_STATUS.ACTIVE:
      return 'bg-[#009E82] text-white';
    case SUBSCRIPTION_STATUS.PAUSED:
      return 'bg-black text-white';
    case SUBSCRIPTION_STATUS.SKIPPED:
      return 'bg-warning text-white';
    case SUBSCRIPTION_STATUS.CANCELLED:
    case SUBSCRIPTION_STATUS.INACTIVE:
      return 'bg-tertiary text-white';
    default:
      return 'bg-tertiary text-white';
  }
};

export const getStatusDisplayLabel = (status: SubscriptionStatus): string => {
  if (isSubscriptionCancelled(status)) return 'Cancelled';
  return status;
};

// ============================================
// Date Formatting
// ============================================

const MONTH_NAMES_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTH_NAMES_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

/** Format date to "Mon D, YYYY" (e.g., "Jan 1, 2026") */
export const formatSMPDate = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    
    const month = MONTH_NAMES_SHORT[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();
    
    return `${month} ${day}, ${year}`;
  } catch {
    return dateString;
  }
};

/** Format date to "Month D, YYYY" (e.g., "January 1, 2026") */
export const formatSMPDateFull = (dateString: string): string => {
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;

    const month = MONTH_NAMES_FULL[date.getMonth()];
    const day = date.getDate();
    const year = date.getFullYear();

    return `${month} ${day}, ${year}`;
  } catch {
    return dateString;
  }
};

// ============================================
// Price Formatting
// ============================================

/** Format price from cents to dollars (e.g., 19900 → "$199.00") */
export const formatPriceFromCents = (priceInCents: number): string => {
  const dollars = priceInCents / 100;
  return `$${dollars.toFixed(2)}`;
};

/** Format price already in dollars (e.g., 199 → "$199.00") */
export const formatPrice = (price: number): string => {
  return `$${price.toFixed(2)}`;
};

// ============================================
// Address Formatting
// ============================================

export const formatShippingAddress = (address: {
  first_name?: string;
  last_name?: string | null;
  street1: string;
  street2?: string | null;
  city: string;
  province: string;
  province_code?: string | null;
  zip: string;
  country?: string;
}): string[] => {
  const name = [address.first_name, address.last_name]
    .filter(Boolean)
    .join(' ');

  return [
    name,
    address.street1,
    address.street2,
    `${address.city}, ${address.province_code || address.province} ${address.zip}`,
  ].filter(Boolean) as string[];
};

/** Format an order address (SMPOrderAddress — different field names from Bold address) */
export const formatOrderAddress = (address: {
  full_name?: string;
  first_name?: string;
  last_name?: string | null;
  address_line1?: string;
  address_line2?: string | null;
  street1?: string;
  street2?: string | null;
  city: string;
  state?: string;
  province_code?: string | null;
  zip: string;
}): string[] => {
  const fullName =
    address.full_name ||
    [address.first_name, address.last_name].filter(Boolean).join(' ');

  return [
    fullName,
    address.address_line1 || address.street1,
    address.address_line2 || address.street2,
    `${address.city}, ${address.state || address.province_code || ''} ${address.zip}`,
  ].filter(Boolean) as string[];
};

/** Calculate new order date by adding 1 month (for skip order flow) */
export const getNewNextOrderDate = (nextOrderDatetime: string): Date => {
  const date = new Date(nextOrderDatetime);
  date.setMonth(date.getMonth() + 1);
  return date;
};

/** Format a Date object to "Mon D, YYYY" */
export const formatDateToSMPDate = (date: Date): string => {
  const month = MONTH_NAMES_SHORT[date.getMonth()];
  const day = date.getDate();
  const year = date.getFullYear();
  return `${month} ${day}, ${year}`;
};

/** Format a Date object to "YYYY-MM-DD" for API payloads */
export const formatDateWithDashes = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// ============================================
// Carrier / Tracking Helpers
// ============================================

const UPS_PATTERN = /^(1Z)[0-9A-Z]{16}$/;
const USPS_PATTERN = /^(94|93|92|95)\d{20}$/;

/** Get carrier tracking URL from a tracking ID */
export const getTrackingUrl = (trackingId: string): string | null => {
  if (UPS_PATTERN.test(trackingId)) {
    return `https://www.ups.com/track?tracknum=${trackingId}`;
  }
  if (USPS_PATTERN.test(trackingId)) {
    return `https://tools.usps.com/go/TrackConfirmAction?tLabels=${trackingId}`;
  }
  return null;
};

/** Get carrier name from a tracking ID */
export const getCarrierName = (trackingId: string): string | null => {
  if (UPS_PATTERN.test(trackingId)) return 'UPS';
  if (USPS_PATTERN.test(trackingId)) return 'USPS';
  return null;
};

// ============================================
// Product Type & Display Helpers
// ============================================

const KIT_TYPE = 'KIT';

/**
 * Maps a product title to a radial gradient background class for the product thumbnail.
 * Gradient colors are matched to each product's brand identity.
 */
export const getProductGradientClass = (title: string): string => {
  const lower = title.toLowerCase();

  if (lower.includes('supplement'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#D5F6D9_0%,#EBEDE0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('biotics') || lower.includes('gut formula'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#EFE6B6_0%,#F0E9E3_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('toothpaste') || lower.includes('gel'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#CCE3E8_0%,#EEE9E1_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  if (lower.includes('lozenge'))
    return 'bg-[radial-gradient(circle_12px_at_71.65%_69.82%,#DCF3FF_0%,#E0E2F0_100%),linear-gradient(180deg,#FFFFFF_0%,rgba(255,255,255,0)_100%)]';

  return '';
};

const BRANDED_PRODUCT_NAMES: Record<string, string> = {
  'Supplements Plan': 'Precision Supplements™',
  'Biotics Plan': 'VRx My·Biotics™ Gut Formula',
  'Lozenges Plan': 'VRx My·Biotics™ Oral Lozenges',
  'Toothpaste/Gel Plan': 'VRx My·Biotics™ Toothpaste & Gel',
};

const PRODUCT_IMAGE_MAP: Record<string, string> = {
  'Supplements Plan': 'supplement.png',
  'Biotics Plan': 'gut_formula.png',
  'Lozenges Plan': 'oral_lozenges.png',
  'Toothpaste/Gel Plan': 'toothpaste_and_gel.png',
};

/**
 * Maps API product titles to their branded equivalents with ™ symbols.
 * If the title already contains ™ or has no known mapping, returns it as-is.
 */
export const formatProductName = (title: string): string => {
  return BRANDED_PRODUCT_NAMES[title] ?? title;
};

export const getProductImageForSMP = (title: string): string => {
  return PRODUCT_IMAGE_MAP[title] ? getFormulaImageUrl(PRODUCT_IMAGE_MAP[title]) : '';
};

/**
 * Returns the supply label for a plan component based on its `type` field.
 * KIT products ship every 12 orders; supplements ship as 30-day supply.
 */
export const getComponentSupplyLabel = (component: { type: string }): string => {
  return component.type === KIT_TYPE ? 'Every 12 orders' : '30-day supply';
};
